import React, { useState } from "react";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import data from "../../data/index.json";
import { Section, SectionHeader, reveal } from "../../components";
import SOBEP from "../../assets/SOBEP.png";
import Progress from "../../assets/Progress.png";
import RDCConsult from "../../assets/RDC_consult.png";

const brands = [
  { id: "1", name: "Bismos", imgUrl: "./img/ILLUSTRATION LOGO BISMOS .png" },
  { id: "2", name: "American Spaces", imgUrl: "./img/american-og.png" },
  { id: "3", name: "Ngozi University", imgUrl: "./img/ngozi-univ.png" },
  { id: "4", name: "Resolution Connect", imgUrl: "./img/resolution.png" },
  { id: "5", name: "IKMC", imgUrl: "./img/IKMC.png" },
  { id: "6", name: "Clauvane Shops", imgUrl: "./img/clauvenshops.png" },
  { id: "7", name: "ETS ROSE", imgUrl: "./img/etsrose.png" },
  { id: "8", name: "SOBEP", imgUrl: SOBEP },
  { id: "9", name: "Progress", imgUrl: Progress },
  { id: "10", name: "RDC Consult", imgUrl: RDCConsult },
];

const Testimonial = () => {
  const [index, setIndex] = useState(0);
  const { t } = useTranslation();
  const { testimonials } = data;
  const current = testimonials[index];

  const go = (delta) =>
    setIndex((prev) => (prev + delta + testimonials.length) % testimonials.length);

  return (
    <Section id="testimonial" tone="paper">
      <SectionHeader
        index="12"
        label={t("Client Success Stories")}
        title={
          <>
            {t("Trusted by")} <em>{t("industry leaders")}</em>
          </>
        }
        aside={
          testimonials.length > 1 && (
            <div className="flex items-center gap-5">
              <span className="label tabular">
                {String(index + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label={t("Previous testimonial")}
                  className="w-11 h-11 flex items-center justify-center border border-[color:var(--rule-strong)] text-ink transition-colors hover:bg-ink hover:text-paper"
                >
                  <HiArrowLeft aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label={t("Next testimonial")}
                  className="w-11 h-11 flex items-center justify-center border border-[color:var(--rule-strong)] text-ink transition-colors hover:bg-ink hover:text-paper"
                >
                  <HiArrowRight aria-hidden />
                </button>
              </div>
            </div>
          )
        }
      />

      {current && (
        <div className="mt-14 md:mt-20 border-t border-[color:var(--rule)] pt-12">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start"
            >
              <div className="lg:col-span-2">
                <div className="w-24 h-24 lg:w-full lg:h-auto lg:aspect-square overflow-hidden bg-paper-warm">
                  <img
                    src={current.imgurl}
                    alt={current.name}
                    loading="lazy"
                    className="img-editorial"
                  />
                </div>
              </div>

              <div className="lg:col-span-10">
                <p className="font-heading text-2xl md:text-[2rem] leading-[1.35] text-ink max-w-[38ch] md:max-w-[46ch]">
                  <span aria-hidden className="text-gold">&ldquo;</span>
                  {t(current.feedback)}
                  <span aria-hidden className="text-gold">&rdquo;</span>
                </p>
                <footer className="mt-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <cite className="not-italic text-base font-semibold text-ink">
                    {current.name}
                  </cite>
                  <span className="label">{t(current.company)}</span>
                </footer>
              </div>
            </motion.blockquote>
          </AnimatePresence>
        </div>
      )}

      {/* ── Partner wall ── */}
      <motion.div {...reveal()} className="mt-16 md:mt-24">
        <p className="label border-t border-[color:var(--rule)] pt-3">
          {t("Organisations we have partnered with")}
        </p>
        <ul className="grid grid-cols-3 sm:grid-cols-5 mt-2">
          {brands.map((brand) => (
            <li
              key={brand.id}
              className="flex items-center justify-center h-24 md:h-28 px-4 border-b border-r border-[color:var(--rule)] [&:nth-child(3n)]:border-r-0 sm:[&:nth-child(3n)]:border-r sm:[&:nth-child(5n)]:border-r-0"
            >
              <img
                src={brand.imgUrl}
                alt={brand.name}
                loading="lazy"
                className="max-h-12 max-w-full object-contain opacity-45 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
              />
            </li>
          ))}
        </ul>
      </motion.div>
    </Section>
  );
};

export default Testimonial;
